// TraitementCongesManager.jsx — Redesign Premium matching other modules
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Search, Filter, Check, X, User, AlertCircle,
    FileText, ChevronDown, CalendarDays, Loader, Eye,
    Download,
} from "lucide-react";
import { API_BASE_URL } from "../../../../../api";

import {
    getManagerInboxRequest,
    managerDecisionRequest,
    clearLeaveRequestMessage,
    exportLeaveRequestsExcelRequest,
} from "../../../../redux/actions/leaveRequestsActions";

import "../TraitementStyles/TraitementAvances.css";

const TraitementCongesManager = () => {
    const dispatch = useDispatch();

    /* ── Redux ──────────────────────────────────────────────── */
    const leaveState = useSelector((state) => state.leaveRequests || {});
    const loading = !!leaveState.loading;
    const apiError = leaveState.error;
    const inbox = leaveState.managerInbox || [];

    /* ── Local state ─────────────────────────────────────────── */
    const [activeTab, setActiveTab] = useState("enCours");
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [isGlobalDelegationEnabled, setIsGlobalDelegationEnabled] = useState(false);
    const [delegationLoading, setDelegationLoading] = useState(false);

    const [showDecisionModal, setShowDecisionModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [decisionComment, setDecisionComment] = useState("");
    const [pendingDecision, setPendingDecision] = useState(null); // "approve" | "reject"
    const [previewDoc, setPreviewDoc] = useState(null); // { url, name, path }

    const [filters, setFilters] = useState({
        typeConge: "",
        dateDebut: "",
        dateFin: "",
    });

    useEffect(() => {
        dispatch(getManagerInboxRequest());
    }, [dispatch]);

    useEffect(() => {
        const loadDelegationSetting = async () => {
            const token = sessionStorage.getItem("token") || sessionStorage.getItem("accessToken");
            const uid = sessionStorage.getItem("uid");

            try {
                const response = await fetch(`${API_BASE_URL}/api/leave-requests/manager/treatment-delegation`, {
                    headers: {
                        Authorization: token ? `Bearer ${token}` : "",
                        uid,
                    },
                });
                if (!response.ok) return;
                const data = await response.json();
                setIsGlobalDelegationEnabled(!!data.enabled);
            } catch (error) {
                console.error("Erreur chargement délégation traitement manager:", error);
            }
        };

        loadDelegationSetting();
    }, []);

    /* ── Helpers ─────────────────────────────────────────────── */
    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        if (isNaN(d)) return dateStr;
        return d.toLocaleDateString("fr-FR", {
            day: "2-digit", month: "short", year: "numeric",
        });
    };

    const mapStatusToTab = (s) => {
        const v = String(s || "").toUpperCase();
        if (v === "PENDING_MANAGER" || v === "POSTPONEMENT_REQUEST") return "enCours";

        if (v === "APPROVED") return "approuve";
        if (v === "REJECTED") return "rejete";
        if (v === "CANCELLED") return "annule";
        if (v === "POSTPONED") return "reporte";
        return "enCours";
    };

    const demandes = useMemo(() => {
        return (inbox || []).map((d) => ({
            id: d.id,
            matricule: d.employeeCode || "—",
            nom: `${d.employeeName || ""} ${d.employeePrenom || ""}`.trim() || "—",
            typeConge: d.absenceTypeNom || "—",
            dateDebut: d.dateDebut,
            dateFin: d.dateFin,
            duree: d.duree ?? 0,
            motif: d.commentaire || " - ",
            justificatif: d.justificatif || null,
            statut: mapStatusToTab(d.status),
            rawStatus: d.status,
            managerDecision: d.managerDecision ?? null,
            managerDecisionAt: d.managerDecisionAt || null,
            managerComment: d.managerComment || null,
            originalDateDebut: d.originalDateDebut,
            originalDateFin: d.originalDateFin,
            dateDemande: d.createdAt,
            justificatifs: d.justificatifs || [],
        }));
    }, [inbox]);

    const getStatutBadge = (demande) => {
        const raw = String(demande.rawStatus || "").toUpperCase();
        if (raw === "POSTPONEMENT_REQUEST") {
            return (
                <span className="tavBadge" style={{ backgroundColor: "#f0f9ff", color: "#0c4a6e" }}>
                    Report en attente
                </span>
            );
        }
        const config = {
            enCours: { label: "En attente", bg: "#f9fefd", color: "#455584" },
            approuve: { label: "Approuvée", bg: "#d4edda", color: "#155724" },
            rejete: { label: "Rejetée", bg: "#f8d7da", color: "#721c24" },
            annule: { label: "Annulée", bg: "#f3f4f6", color: "#6b7280" },
            reporte: { label: "Reportée", bg: "#e0f2fe", color: "#0369a1" },
        };
        const c = config[demande.statut] || config.enCours;
        return (
            <span className="tavBadge" style={{ backgroundColor: c.bg, color: c.color }}>
                {c.label}
            </span>
        );
    };


    const filtered = useMemo(() => {
        let list = demandes.filter((d) => d.statut === activeTab);

        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            list = list.filter(
                (d) =>
                    d.nom.toLowerCase().includes(s) ||
                    d.matricule.toLowerCase().includes(s) ||
                    d.typeConge.toLowerCase().includes(s)
            );
        }

        if (filters.typeConge) list = list.filter((d) => d.typeConge === filters.typeConge);
        if (filters.dateDebut) {
            list = list.filter((d) => {
                if (!d.dateDebut) return true;
                const date = new Date(d.dateDebut).toISOString().split("T")[0];
                return date >= filters.dateDebut;
            });
        }
        if (filters.dateFin) {
            list = list.filter((d) => {
                if (!d.dateFin) return true;
                const date = new Date(d.dateFin).toISOString().split("T")[0];
                return date <= filters.dateFin;
            });
        }
        return list;
    }, [demandes, activeTab, searchTerm, filters]);

    const tabs = useMemo(() => [
        { id: "enCours", label: "En attente", count: demandes.filter(d => d.statut === "enCours").length },
        { id: "approuve", label: "Validées", count: demandes.filter(d => d.statut === "approuve").length },
        { id: "rejete", label: "Refusées", count: demandes.filter(d => d.statut === "rejete").length },
        { id: "annule", label: "Annulées", count: demandes.filter(d => d.statut === "annule").length },
        { id: "reporte", label: "Reportées", count: demandes.filter(d => d.statut === "reporte").length },
    ], [demandes]);

    const typesConges = useMemo(() => [...new Set(demandes.map(d => d.typeConge).filter(Boolean))], [demandes]);

    /* ── Actions ─────────────────────────────────────────────── */
    const handleOpenDecision = (demande, decision) => {
        setSelectedDemande(demande);
        setPendingDecision(decision);
        setDecisionComment("");
        setShowDecisionModal(true);
    };

    const confirmDecision = () => {
        if (!selectedDemande) return;
        const approved = pendingDecision === "approve";
        if (!approved && !decisionComment.trim()) return;

        dispatch(managerDecisionRequest({
            id: selectedDemande.id,
            approved,
            comment: decisionComment || "",
        }));
        dispatch(clearLeaveRequestMessage());
        closeDecision();
    };

    const handleToggleGlobalDelegation = async (enabled) => {
        const confirmed = window.confirm(
            enabled
                ? "Activer la délégation ? Les demandes en attente seront transmises à l'Admin RH."
                : "Désactiver la délégation automatique vers l'Admin RH ?"
        );
        if (!confirmed) return;

        const token = sessionStorage.getItem("token") || sessionStorage.getItem("accessToken");
        const uid = sessionStorage.getItem("uid");

        setDelegationLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/leave-requests/manager/treatment-delegation?enabled=${enabled}`, {
                method: "POST",
                headers: {
                    Authorization: token ? `Bearer ${token}` : "",
                    uid,
                },
            });
            if (!response.ok) throw new Error("Impossible de mettre à jour la délégation.");
            const data = await response.json();
            setIsGlobalDelegationEnabled(!!data.enabled);
            dispatch(getManagerInboxRequest());
        } catch (error) {
            console.error("Erreur délégation traitement manager:", error);
            window.alert(error.message || "Impossible de mettre à jour la délégation.");
        } finally {
            setDelegationLoading(false);
        }
    };

    const closeDecision = () => {
        setShowDecisionModal(false);
        setDecisionComment("");
        setSelectedDemande(null);
        setPendingDecision(null);
    };

    const handleDownload = (path) => {
        if (!path) return;
        const url = `${API_BASE_URL}/api/files/download?path=${encodeURIComponent(path)}`;
        window.open(url, "_blank");
    };

    const showDetails = (demande) => { setSelectedDemande(demande); setShowDetailsModal(true); };
    const closeDetails = () => { setShowDetailsModal(false); setSelectedDemande(null); };

    const handlePreview = (path) => {
        if (!path) return;
        const url = `${API_BASE_URL}/api/files/download?path=${encodeURIComponent(path)}&inline=true`;
        setPreviewDoc({
            url,
            name: path.split("/").pop(),
            path: path
        });
    };

    const closePreview = () => setPreviewDoc(null);

    /* ── Render ──────────────────────────────────────────────── */
    if (loading && demandes.length === 0) {
        return (
            <div className="tavPage">
                <div className="tavWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tavPage">
            <div className="tavWrap">
                {/* Header */}
                <div className="tavHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                    <div>
                        <h1>Traitement des demandes de congés</h1>
                        <p>Validez les absences et congés des membres de votre équipe</p>
                    </div>
                    <div className="tavDelegationSetting">
                        <div>
                            <div className="tavDelegationSetting__title">Délégation Admin RH</div>
                            <div className="tavDelegationSetting__sub">Transmettre mon traitement de congés à l'admin RH</div>
                        </div>
                        <label className="tavSwitch" title="Délégation Admin RH">
                            <input
                                type="checkbox"
                                checked={isGlobalDelegationEnabled}
                                disabled={delegationLoading}
                                onChange={(e) => handleToggleGlobalDelegation(e.target.checked)}
                            />
                            <span />
                        </label>
                    </div>
                </div>

                {/* Search + Filters */}
                <div className="tavCard">
                    <div className="tavControls">
                        <div className="tavSearch">
                            <Search size={18} />
                            <input
                                type="text"
                                placeholder="Rechercher par collaborateur, matricule, type..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button type="button" className="tavFilterBtn" onClick={() => setShowFilters(v => !v)}>
                            <Filter size={16} /> Filtres
                            <ChevronDown size={16} className={`tcChevron ${showFilters ? "is-open" : ""}`} />
                        </button>
                        <button
                            type="button"
                            className="tavFilterBtn"
                            style={{ marginLeft: "auto", background: "#10b981", color: "white", border: "none" }}
                            onClick={() => dispatch(exportLeaveRequestsExcelRequest("manager_inbox"))}
                        >
                            <Download size={16} />
                            Exporter Excel
                        </button>
                    </div>

                    {showFilters && (
                        <div className="tavFilters">
                            <div className="tavField">
                                <label>Type de congé</label>
                                <select value={filters.typeConge} onChange={e => setFilters(p => ({ ...p, typeConge: e.target.value }))}>
                                    <option value="">Tous</option>
                                    {typesConges.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div className="tavField">
                                <label>Date début</label>
                                <input type="date" value={filters.dateDebut} onChange={e => setFilters(p => ({ ...p, dateDebut: e.target.value }))} />
                            </div>
                            <div className="tavField">
                                <label>Date de fin</label>
                                <input type="date" value={filters.dateFin} onChange={e => setFilters(p => ({ ...p, dateFin: e.target.value }))} />
                            </div>
                        </div>
                    )}
                </div>

                {/* Tabs + Table */}
                <div className="tavCard tavCard--noPad">
                    <div className="tavTabs">
                        {tabs.map(tab => (
                            <button key={tab.id} type="button"
                                className={`tavTab ${activeTab === tab.id ? "is-active" : ""}`}
                                onClick={() => setActiveTab(tab.id)}>
                                {tab.label}
                                <span className={`tavCount ${activeTab === tab.id ? "is-active" : ""}`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    <div className="tavTableWrap">
                        {filtered.length === 0 ? (
                            <div className="tavEmpty">
                                <AlertCircle size={46} />
                                <div>Aucune demande trouvée</div>
                            </div>
                        ) : (
                            <table className="tavTable">
                                <thead>
                                    <tr>
                                        <th>Matricule</th>
                                        <th>Collaborateur</th>
                                        <th>Type</th>
                                        <th>{activeTab === "reporte" ? "Nouvelle Période" : "Période"}</th>
                                        {activeTab === "reporte" && <th>Ancienne Période</th>}
                                        <th>Durée</th>
                                        <th>Statut</th>
                                        <th className="is-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((d) => (
                                        <tr key={d.id} onClick={() => showDetails(d)} style={{ cursor: "pointer" }}>
                                            <td><span className="tavPrimary">{d.matricule}</span></td>
                                            <td><span className="tavPrimary">{d.nom}</span></td>
                                            <td>{d.typeConge}</td>
                                            <td>
                                                <div className="tavPeriod">
                                                    <span className="tavPrimary">{formatDate(d.dateDebut)} → {formatDate(d.dateFin)}</span>
                                                </div>
                                            </td>
                                            {activeTab === "reporte" && (
                                                <td>
                                                    <div className="tavPeriod" style={{ textDecoration: "line-through", opacity: 0.7 }}>
                                                        <span className="tavMuted">{formatDate(d.originalDateDebut)} → {formatDate(d.originalDateFin)}</span>
                                                    </div>
                                                </td>
                                            )}
                                            <td><b>{d.duree} j</b></td>
                                            <td>{getStatutBadge(d)}</td>

                                            <td className="is-right" onClick={e => e.stopPropagation()}>
                                                {d.statut === "enCours" ? (
                                                    <div className="tavRowActions">
                                                        <button type="button" className="tavActionBtn tavActionBtn--ok"
                                                            onClick={() => handleOpenDecision(d, "approve")} title="Approuver">
                                                            <Check size={16} />
                                                        </button>
                                                        <button type="button" className="tavActionBtn tavActionBtn--no"
                                                            onClick={() => handleOpenDecision(d, "reject")} title="Refuser">
                                                            <X size={16} />
                                                        </button>
                                                        <button type="button" className="tavActionBtn"
                                                            onClick={() => showDetails(d)} title="Détails"
                                                            style={{ background: "#eff6ff", color: "#2f478f", border: "1px solid #bfdbfe" }}>
                                                            <Eye size={16} />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button type="button" className="tavActionBtn"
                                                        onClick={() => showDetails(d)} title="Détails"
                                                        style={{ background: "#eff6ff", color: "#2f478f", border: "1px solid #bfdbfe" }}>
                                                        <Eye size={16} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* Decision Modal */}
            {showDecisionModal && selectedDemande && (
                <div className="tavModal">
                    <div className="tavModal__panel tavModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tavModal__header">
                            <div className="tavModal__headLeft">
                                <div className={`tcModal__icon ${pendingDecision === "reject" ? "tavModal__icon--danger" : ""}`}>
                                    {pendingDecision === "approve" ? <Check size={18} /> : <AlertCircle size={18} />}
                                </div>
                                <div>
                                    <div className="tavModal__title">
                                        {pendingDecision === "approve" ? "Approuver la demande" : "Refuser la demande"}
                                    </div>
                                    <div className="tavModal__sub">{selectedDemande.nom} — {selectedDemande.typeConge}</div>
                                </div>
                            </div>
                            <button type="button" className="tavIconBtn" onClick={closeDecision}><X size={18} /></button>
                        </div>
                        <div className="tavModal__body">
                            <label className="tavLabel">Commentaire {pendingDecision === "reject" && <span className="tavReq">*</span>}</label>
                            <textarea
                                className="tavTextarea"
                                rows={4}
                                value={decisionComment}
                                onChange={e => setDecisionComment(e.target.value)}
                                placeholder="Ajouter un commentaire pour le collaborateur..."
                            />
                            {pendingDecision === "reject" && !decisionComment.trim() && (
                                <p style={{ color: "#ef4444", fontSize: 11, marginTop: 4 }}>Un motif est obligatoire pour un refus.</p>
                            )}
                        </div>
                        <div className="tavModal__footer">
                            <button className="tavBtn tavBtn--ghost" onClick={closeDecision}>Annuler</button>
                            <button className={`tcBtn ${pendingDecision === "approve" ? "tavBtn--ok" : "tavBtn--danger"}`}
                                onClick={confirmDecision}
                                disabled={pendingDecision === "reject" && !decisionComment.trim()}>
                                Confirmer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Details Modal */}
            {showDetailsModal && selectedDemande && (
                <div className="tavModal">
                    <div className="tavModal__panel" role="dialog" aria-modal="true">
                        <div className="tavModal__header">
                            <div className="tavModal__headLeft">
                                <div className="tavModal__icon">
                                    <CalendarDays size={18} />
                                </div>
                                <div>
                                    <div className="tavModal__title">Demande de congé #{selectedDemande.id}</div>
                                    <div className="tavModal__sub">Demande du {formatDate(selectedDemande.dateDebut)}</div>
                                </div>
                            </div>
                            <button type="button" className="tavIconBtn" onClick={closeDetails}>
                                <X size={18} />
                            </button>
                        </div>
                        
                        <div className="tavModal__body">
                            <div className="tavGrid2">
                                {/* Collaborateur */}
                                <div className="tavPanel">
                                    <div className="tavPanel__title">
                                        <User size={16} /> Collaborateur
                                    </div>
                                    <div className="tavKv"><span>Matricule</span><b>{selectedDemande.matricule}</b></div>
                                    <div className="tavKv"><span>Nom</span><b>{selectedDemande.nom}</b></div>
                                    <div className="tavKv"><span>Date de la demande</span><b>{formatDate(selectedDemande.dateDemande)}</b></div>
                                </div>

                                {/* Détails congé */}
                                <div className="tavPanel">
                                    <div className="tavPanel__title">
                                        <CalendarDays size={16} /> Détails du congé
                                    </div>
                                    <div className="tavKv"><span>Type</span><b>{selectedDemande.typeConge}</b></div>
                                    <div className="tavKv"><span>{selectedDemande.originalDateDebut ? "Nouvelle Date début" : "Date de début"}</span><b>{formatDate(selectedDemande.dateDebut)}</b></div>
                                    <div className="tavKv"><span>{selectedDemande.originalDateFin ? "Nouvelle Date de fin" : "Date de fin"}</span><b>{formatDate(selectedDemande.dateFin)}</b></div>
                                    {selectedDemande.originalDateDebut && (
                                        <div className="tavKv" style={{ background: "#f9fefd", padding: "4px 8px", borderRadius: 6, marginTop: 4 }}>
                                            <span style={{ color: "#455584" }}>Ancienne période</span>
                                            <b style={{ color: "#455584", textDecoration: "line-through" }}>
                                                {formatDate(selectedDemande.originalDateDebut)} → {formatDate(selectedDemande.originalDateFin)}
                                            </b>
                                        </div>
                                    )}
                                    <div className="tavKv"><span>Durée</span><b>{selectedDemande.duree} jour{selectedDemande.duree > 1 ? "s" : ""}</b></div>
                                </div>

                                {/* Motif */}
                                <div className="tavPanel tavPanel--full">
                                    <div className="tavPanel__title">
                                        <FileText size={16} /> Motif de la demande
                                    </div>
                                    <div className="tavText">{selectedDemande.motif || " - "}</div>
                                </div>

                                {/* Justificatifs */}
                                {(selectedDemande.justificatif || (selectedDemande.justificatifs && selectedDemande.justificatifs.length > 0)) && (
                                    <div className="tavPanel tavPanel--full">
                                        <div className="tavPanel__title">
                                            <FileText size={16} /> Justificatifs fournis
                                        </div>
                                        <div className="tnfDocs" style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "10px" }}>
                                            {selectedDemande.justificatif && !selectedDemande.justificatifs?.includes(selectedDemande.justificatif) && (
                                                <div className="tnfDoc" onClick={() => handlePreview(selectedDemande.justificatif)} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", padding: "8px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", transition: "all 0.2s" }}>
                                                    <FileText size={18} color="#64748b" />
                                                    <div style={{ flex: 1, fontSize: "13px", fontWeight: "500", color: "#334155" }}>
                                                        {selectedDemande.justificatif.split("/").pop()}
                                                    </div>
                                                    <Eye size={16} color="#2f478f" />
                                                </div>
                                            )}
                                            {selectedDemande.justificatifs?.map((j, idx) => (
                                                <div key={idx} className="tnfDoc" onClick={() => handlePreview(j)} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", padding: "8px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", transition: "all 0.2s" }}>
                                                    <FileText size={18} color="#64748b" />
                                                    <div style={{ flex: 1, fontSize: "13px", fontWeight: "500", color: "#334155" }}>
                                                        {j.split("/").pop()}
                                                    </div>
                                                    <Eye size={16} color="#2f478f" />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Décision Manager */}
                                {selectedDemande.managerDecision !== null && (
                                    <div className={`tcPanel tavPanel--full ${selectedDemande.statut === "approuve" ? "tavValidOk" : "tavValidNo"}`}>
                                        <div className="tavPanel__title">
                                            {selectedDemande.statut === "approuve" ? <Check size={16} /> : <X size={16} />}
                                            {selectedDemande.statut === "approuve" ? " Demande approuvée" : " Demande refusée"}
                                        </div>
                                        {selectedDemande.managerDecisionAt && (
                                            <div className="tavKv"><span>Date</span><b>{formatDate(selectedDemande.managerDecisionAt)}</b></div>
                                        )}
                                        {selectedDemande.managerComment && (
                                            <div className="tavRejectBox">
                                                <div className="tavRejectLabel">
                                                    {selectedDemande.statut === "approuve" ? "Commentaire" : "Motif du refus"}
                                                </div>
                                                <div className="tavRejectText">{selectedDemande.managerComment}</div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="tavModal__footer">
                            {selectedDemande.statut === "enCours" ? (
                                <>
                                    <button 
                                        className="tavBtn tavBtn--danger"
                                        onClick={() => { closeDetails(); handleOpenDecision(selectedDemande, "reject"); }}
                                        disabled={loading}
                                    >
                                        Refuser
                                    </button>
                                    <button 
                                        className="tavBtn tavBtn--ok"
                                        onClick={() => { closeDetails(); handleOpenDecision(selectedDemande, "approve"); }}
                                        disabled={loading}
                                    >
                                        Approuver
                                    </button>
                                    <button className="tavBtn tavBtn--ghost" onClick={closeDetails}>Fermer</button>
                                </>
                            ) : (
                                <button className="tavBtn tavBtn--ghost" onClick={closeDetails}>Fermer</button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            {previewDoc && (
                <div className="tavModal" style={{ zIndex: 10001 }}>
                    <div className="tavModal__panel tavModal__panel--wide" style={{ maxWidth: "90vw", width: "1000px" }}>
                        <div className="tavModal__header">
                            <div className="tavModal__headLeft">
                                <div className="tavModal__icon"><FileText size={18} /></div>
                                <div>
                                    <div className="tavModal__title">Aperçu du document</div>
                                    <div className="tavModal__sub">{previewDoc.name}</div>
                                </div>
                            </div>
                            <button type="button" className="tavIconBtn" onClick={closePreview}><X size={18} /></button>
                        </div>
                        <div className="tavModal__body" style={{ height: "75vh", padding: 0, overflow: "hidden", background: "#f1f5f9", display: "flex", justifyContent: "center", alignItems: "center" }}>
                            {previewDoc.name.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
                                    <img src={previewDoc.url} alt="Aperçu" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 4, boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1)" }} />
                                </div>
                            ) : previewDoc.name.match(/\.pdf$/i) ? (
                                <iframe src={previewDoc.url} title="Aperçu PDF" width="100%" height="100%" style={{ border: "none" }} />
                            ) : (
                                <div style={{ textAlign: "center", padding: 40 }}>
                                    <AlertCircle size={48} color="#64748b" style={{ marginBottom: 16 }} />
                                    <div style={{ fontWeight: 600, color: "#1e293b", marginBottom: 8 }}>Aperçu non disponible</div>
                                    <p style={{ color: "#64748b", marginBottom: 24 }}>Ce type de fichier ne peut pas être prévisualisé directement.</p>
                                    <button className="tavBtn tavBtn--primary" onClick={() => handleDownload(previewDoc.path)}>
                                        <Download size={16} /> Télécharger pour consulter
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="tavModal__footer">
                            <button className="tavBtn tavBtn--ghost" onClick={closePreview}>Fermer</button>
                            <button className="tavBtn tavBtn--primary" onClick={() => handleDownload(previewDoc.path)}>
                                <Download size={16} /> Télécharger le document
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementCongesManager;
